/**
 * Report export. A report is modelled as one or more named tables, so the
 * same structure serves CSV, XLSX and the print/PDF view, the formats differ
 * in packaging, never in the numbers.
 */

export interface ExportTable {
  name: string;
  columns: string[];
  rows: (string | number | null)[][];
}

const csvCell = (value: string | number | null): string => {
  if (value == null) return "";
  const text = String(value);
  // A leading =, +, - or @ makes Excel evaluate the cell as a formula. Prefix
  // with a quote so an imported value can never execute.
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export const tablesToCsv = (tables: ExportTable[]): string =>
  tables
    .map((table) =>
      [
        table.name,
        table.columns.map(csvCell).join(","),
        ...table.rows.map((row) => row.map(csvCell).join(",")),
      ].join("\n"),
    )
    .join("\n\n");

export const downloadBlob = (
  content: BlobPart[],
  filename: string,
  type: string,
) => {
  const url = URL.createObjectURL(new Blob(content, { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const exportCsv = (tables: ExportTable[], filename: string) =>
  // The BOM is what makes Excel read UTF-8 rather than mangling accents.
  downloadBlob(
    ["﻿", tablesToCsv(tables)],
    `${filename}.csv`,
    "text/csv;charset=utf-8",
  );

export const exportXlsx = async (tables: ExportTable[], filename: string) => {
  // Loaded on demand: the workbook writer is dead weight for the 95% of
  // report views that are never exported.
  const { buildXlsx } = await import("./xlsx");
  const blob = buildXlsx(tables);
  downloadBlob([blob], `${filename}.xlsx`, blob.type);
};

/**
 * PDF via the browser's own print pipeline ("Save as PDF"). No renderer is
 * bundled, the print stylesheet hides the app chrome so what prints is the
 * report, and the user gets real selectable text at their own paper size.
 */
export const printReport = () => window.print();

/** Cents → a plain decimal string, so a spreadsheet reads it as a number. */
export const centsColumn = (cents: number | null | undefined): number =>
  cents == null ? 0 : cents / 100;
